import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import { useWebSocketContext } from '../../../contexts';
import { useApiData } from '../../../hooks';
import { END_POINTS } from '../../../services';
import { DAY_DROP_DOWN_OPTIONS } from '../../../utils/date';
import {
  DASHBOARD_ACTION_TYPES,
  DashboardAction, // DashboardActionTypes,
  DashboardContextProps,
  DashboardState,
  ParamVersion,
} from '../Dashboard.types';
import {
  GRAPH_GENERATE_STATUS,
  SHIFT_MANAGE_STATUS,
  facilityActions,
  useFacilityContext,
} from './FacilityProvider';
import { FacilitySnapShot } from './FacilityProvider.types';
import ShiftProvider from './ShiftProvider';

const initialStates: DashboardState = {
  scenarios: {
    isLoading: false,
    data: [],
    selected: '',
  },
  days: {
    isLoading: false,
    data: DAY_DROP_DOWN_OPTIONS,
    selected: '0',
  },
  paramVersions: {
    isLoading: false,
    data: [],
    selected: '',
  },
};
const defaultValue: DashboardContextProps = {
  state: initialStates,
  dispatch: () => {},
  activeFacilitySnapShot: {
    clientId: undefined,
    userGraphGenerationStatus: GRAPH_GENERATE_STATUS.IDLE_GENERATED_GRAPH,
    userShiftManageStatus: SHIFT_MANAGE_STATUS.IDLE,
    activeParamVersionId: '',
  },
};

const DashboardContext = createContext<DashboardContextProps>(defaultValue);

export const useDashboardContext = () => useContext(DashboardContext);
export const DashboardProvider: React.FC<{
  children: React.ReactNode;
  facility: string;
}> = ({ children, facility }) => {
  const {
    state: { facilitySnapshots },
    dispatch: facilityDispatch,
  } = useFacilityContext();

  const [state, dispatch] = useReducer(dashboardReducer, initialStates);
  const { socketInstance } = useWebSocketContext();
  const [prevScenarioList, setPrevScenarioList] = useState<
    Partial<DashboardState['scenarios']>
  >({});
  // const { sessionId } = useWebSocketContext();
  const activeFacilitySnapShot: FacilitySnapShot = {
    userGraphGenerationStatus: GRAPH_GENERATE_STATUS.IDLE_GENERATED_GRAPH,
    userShiftManageStatus: SHIFT_MANAGE_STATUS.IDLE,
    clientId: undefined,
    ...(facilitySnapshots?.[facility] || {}),
  };
  const [isRegenerated, setIsRegenerated] = useState(false);

  const [prevClientId, setPrevClientId] = useState(
    activeFacilitySnapShot.clientId
  );
  if (prevClientId !== activeFacilitySnapShot.clientId) {
    setPrevClientId(activeFacilitySnapShot.clientId);
    setIsRegenerated(true);
  }
  const [prevParamVersion, setPrevParamVersion] = useState<ParamVersion[]>([]);
  const { data: _versionList = [], reValidate: reValidateParamVersionList } =
    useApiData(END_POINTS.SOT.versions, {
      params: { facility_code: facility },
    });
  const paramVersionList = useMemo(() => {
    return [
      { id: '', name: 'Default' },
      ...(_versionList?.map?.((d: any, index: number) => {
        return { id: d?.VersionId, name: d?.VersionName || 'unnamed' };
      }) || []),
    ];
  }, [_versionList]);

  if (JSON.stringify(prevParamVersion) !== JSON.stringify(paramVersionList)) {
    setPrevParamVersion(paramVersionList);
    dispatch(dashboardActions.setParamVersions(paramVersionList));
  }

  const {
    data: scenarioRes,
    isLoading,
    reValidate,
  } = useApiData(
    END_POINTS.DASHBOARD.getScenarios,
    {
      params: {
        facility_code: facility,
        client_id: prevClientId,
      },
    } //client_id
  );

  const scenarioList: Partial<DashboardState['scenarios']> = useMemo(() => {
    const scenarioData =
      (Array.isArray(scenarioRes) &&
        scenarioRes?.map?.((e: string, index) => ({
          id: e,
          label: `Shift Option ${++index}`,
        }))) ||
      [];
    return {
      isLoading,
      data: scenarioData,
      selected: scenarioData?.[0]?.id || '',
    };
  }, [scenarioRes, isLoading]);

  if (JSON.stringify(prevScenarioList) !== JSON.stringify(scenarioList)) {
    dispatch(dashboardActions.setScenarios(scenarioList));
    setPrevScenarioList(scenarioList);
  }
  useEffect(() => {
    if (
      activeFacilitySnapShot.userGraphGenerationStatus ===
        GRAPH_GENERATE_STATUS.APPLY_GENERATED_GRAPH ||
      activeFacilitySnapShot.userGraphGenerationStatus ===
        GRAPH_GENERATE_STATUS.DEFAULT_GENERATE_GRAPH
    ) {
      if (!isRegenerated) reValidate();

      setIsRegenerated(false);
      facilityDispatch(
        facilityActions.updateFacilityWiseGraphStatus(
          facility,
          GRAPH_GENERATE_STATUS.IDLE_GENERATED_GRAPH
        )
      );
    }
  }, [
    activeFacilitySnapShot.userGraphGenerationStatus,
    // sessionId,
    activeFacilitySnapShot.clientId,
    facilityDispatch,
    reValidate,
    facility,
    isRegenerated,
  ]);
  /*******SETTING CLIENT_ID FROM WS */

  useEffect(() => {
    const handleMessage = (e: any) => {
      // let messageData = e?.data;
      let messageData = e;
      console.info('WS Message Data listener', messageData);

      try {
        // messageData = JSON.parse(e?.data ?? '');

        // if (messageData?.status?.toLowerCase?.() === 'start') {
        //   facilityDispatch(
        //     facilityActions.updateFacilityWiseGraphStatus(
        //       messageData?.facility,
        //       GRAPH_GENERATE_STATUS.IN_PROGRESS_GENERATE_GRAPH
        //     )
        //   );
        // } else
        if (messageData?.status?.toLowerCase?.() === 'success') {
          console.info('WS Message success Data listener', messageData);
          facilityDispatch(
            facilityActions.updateFacilityWiseGraphStatus(
              messageData?.facility,
              GRAPH_GENERATE_STATUS.SUCCESS_GENERATE_GRAPH
            )
          );
        } else if (messageData?.status?.toLowerCase?.() === 'failed') {
          facilityDispatch(
            facilityActions.updateFacilityWiseGraphStatus(
              messageData?.facility,
              GRAPH_GENERATE_STATUS.ERROR_GENERATE_GRAPH
            )
          );
        }
      } catch (error) {
        messageData = e;
        console.error('WS Message Listener Error 💥', error, e);
      }
    };

    socketInstance?.on('sot', handleMessage);
    return () => {
      socketInstance?.off('sot', handleMessage);
    };
  }, [socketInstance, facilityDispatch]);
  /********************************************** */

  return (
    <DashboardContext.Provider
      value={{
        state,
        dispatch,
        activeFacilitySnapShot,
        reValidateParamVersionList,
      }}
    >
      <ShiftProvider scenario={state?.scenarios?.selected}>
        {children}
      </ShiftProvider>
    </DashboardContext.Provider>
  );
};
export const dashboardActions = {
  setScenarios: ({ data, isLoading }: Partial<DashboardState['scenarios']>) => {
    let obj: Partial<DashboardState['scenarios']> = {};
    if (typeof data !== 'undefined') {
      obj.data = data;
      if (Array.isArray(data) && data.length > 0) {
        obj.selected = data?.[0]?.id || '';
      } else obj.selected = '';
    }
    if (typeof isLoading !== 'undefined') {
      obj.isLoading = isLoading;
    }
    return {
      type: DASHBOARD_ACTION_TYPES.SET_SCENARIO_LIST,
      payload: obj,
    };
  },
  setSelectedScenario: (scenario?: string | number) => {
    return {
      type: DASHBOARD_ACTION_TYPES.SET_SELECTED_SCENARIO,
      payload: scenario,
    };
  },
  setSelectedDay: (day: string | number) => {
    return {
      type: DASHBOARD_ACTION_TYPES.SET_SELECTED_DAY,
      payload: typeof day === 'number' ? String(day) : day,
    };
  },
  setSelectedParamVersion: (versionId: ParamVersion['id']) => {
    return {
      type: DASHBOARD_ACTION_TYPES.SET_SELECTED_PARAM_VERSION,
      payload: versionId,
    };
  },
  setParamVersions: (v: ParamVersion[] = []) => {
    return {
      type: DASHBOARD_ACTION_TYPES.SET_PARAM_VERSIONS,
      payload: v,
    };
  },
};
function dashboardReducer(
  state: DashboardState,
  action: DashboardAction
): DashboardState {
  const { payload, type } = action;
  switch (type) {
    case DASHBOARD_ACTION_TYPES.SET_SCENARIO_LIST: {
      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          ...payload,
        },
      };
    }
    case DASHBOARD_ACTION_TYPES.SET_SELECTED_SCENARIO: {
      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          selected: payload,
        },
      };
    }
    case DASHBOARD_ACTION_TYPES.SET_SELECTED_DAY: {
      return {
        ...state,
        days: {
          ...state.days,
          selected: payload,
        },
      };
    }
    case DASHBOARD_ACTION_TYPES.SET_PARAM_VERSIONS: {
      return {
        ...state,
        paramVersions: {
          ...state.paramVersions,
          data: [...payload],
        },
      };
    }
    case DASHBOARD_ACTION_TYPES.SET_SELECTED_PARAM_VERSION: {
      return {
        ...state,
        paramVersions: {
          ...state.paramVersions,
          selected: payload,
        },
      };
    }
    default: {
      throw Error('Unknown action: ' + action.type);
    }
  }
}
